import {
    seasonNames,
    QUADRANT_CHART_DEFAULT_ORIENTATION,
    QUADRANT_CHART_ROTATED_ORIENTATION,
} from 'resources/common-values';
import focusSVG from 'styles/icons/focus.svg';
import focusPNG from 'styles/icons/focus.png';
import FeatureUtils from 'utils/features';

const quadrantColor = {
    lowAchievementHighGrowth: '#fce2a8',
    highAchievementHighGrowth: '#b2ddba',
    highAchievementLowGrowth: '#fefbc4',
    lowAchievementLowGrowth: '#fbd6d4',
};

const labelTitleStyle = {
    fontSize: '.75rem',
};

const labelTickStyle = {
    fontSize: '.95rem',
};

export function getSeries(orientation, achievementPercentile, growthPercentile, plotSize) {
    return getPlotPoint(orientation, achievementPercentile, growthPercentile, plotSize).concat(
        getQuadrants(orientation)
    );
}

function getPlotPoint(orientation, achievementPercentile, growthPercentile, plotSize) {
    const data =
        orientation === QUADRANT_CHART_DEFAULT_ORIENTATION
            ? [{ x: achievementPercentile, y: growthPercentile }]
            : [{ x: growthPercentile, y: achievementPercentile }];
    return [
        {
            name: 'Growth & Achievement',
            color: '#185aa9',
            data: data,
            marker: {
                enabled: !!achievementPercentile && !!growthPercentile,
                symbol: FeatureUtils.isIE() ? 'url(' + focusPNG + ')' : 'url(' + focusSVG + ')',
                height: plotSize,
                width: plotSize,
            },
            zIndex: 5,
            states: {
                hover: {
                    enabled: false,
                },
            },
        },
    ];
}

function getQuadrantStates() {
    return {
        hover: {
            enabled: false,
            marker: {
                states: {
                    hover: {
                        enabled: false,
                    },
                },
            },
        },
    };
}

function getQuadrants(orientation) {
    let defaultOrientation = [
        quadrantColor.lowAchievementHighGrowth,
        quadrantColor.highAchievementHighGrowth,
        quadrantColor.lowAchievementLowGrowth,
        quadrantColor.highAchievementLowGrowth,
    ];
    let rotatedOrientation = [
        quadrantColor.highAchievementLowGrowth,
        quadrantColor.highAchievementHighGrowth,
        quadrantColor.lowAchievementLowGrowth,
        quadrantColor.lowAchievementHighGrowth,
    ];
    let quadrants = [
        {
            name: 'top-left',
            data: [
                { x: 50, y: 50 },
                { x: 0, y: 50 },
                { x: 0, y: 100 },
                { x: 50, y: 100 },
                { x: 50, y: 50 },
            ],
            zIndex: 1,
            states: getQuadrantStates(),
        },
        {
            name: 'top-right',
            data: [
                { x: 50, y: 50 },
                { x: 50, y: 100 },
                { x: 100, y: 100 },
                { x: 100, y: 50 },
                { x: 50, y: 50 },
            ],
            zIndex: 1,
            states: getQuadrantStates(),
        },
        {
            name: 'bottom-left',
            data: [
                { x: 50, y: 50 },
                { x: 0, y: 50 },
                { x: 0, y: 0 },
                { x: 50, y: 0 },
                { x: 50, y: 50 },
            ],
            zIndex: 0,
            states: getQuadrantStates(),
        },
        {
            name: 'bottom-right',
            data: [
                { x: 50, y: 50 },
                { x: 50, y: 0 },
                { x: 100, y: 0 },
                { x: 100, y: 50 },
                { x: 50, y: 50 },
            ],
            zIndex: 0,
            states: getQuadrantStates(),
        },
    ];

    let quadrantColors = orientation === QUADRANT_CHART_DEFAULT_ORIENTATION ? defaultOrientation : rotatedOrientation;
    for (let quadrantIndex = 0; quadrantIndex < 4; quadrantIndex++) {
        quadrants[quadrantIndex].color = quadrantColors[quadrantIndex];
        quadrants[quadrantIndex].fillOpacity = 1;
        quadrants[quadrantIndex].lineColor = '#808080';
        quadrants[quadrantIndex].lineWidth = 1;
        quadrants[quadrantIndex].type = 'area';
        quadrants[quadrantIndex].animation = false;
        quadrants[quadrantIndex].enableMouseTracking = false;
    }

    return quadrants;
}

export function getXAxis(orientation) {
    return [
        {
            min: 0,
            max: 100,
            tickInterval: 100,
            tickLength: 0,
            lineWidth: 0,
            showFirstLabel: false,
            showLastLabel: false,
            title: {
                text: getAxisTitle(true, orientation),
                useHTML: true,
                offset: 10,
                align: 'middle',
                style: labelTitleStyle,
            },
        },
        {
            min: 0,
            max: 100,
            tickInterval: 50,
            tickLength: 0,
            lineWidth: 0,
            opposite: true,
            showFirstLabel: false,
            showLastLabel: false,
            title: {
                enabled: false,
            },
            labels: {
                enabled: true,
                useHTML: true,
                y: -5,
                style: labelTickStyle,
                formatter: function() {
                    if (!this.isFirst && !this.isLast) {
                        return '50<sup>th</sup>';
                    }
                },
            },
        },
    ];
}

export function getYAxis(orientation) {
    return [
        {
            categories: ['Low', 'High'],
            className: 'yaxis-left',
            min: 0,
            max: 100,
            tickInterval: 100,
            tickLength: 0,
            lineWidth: 1,
            lineColor: '#808080',
            labels: {
                enabled: true,
                useHTML: true,
                x: -5,
                reserveSpace: true,
                style: labelTickStyle,
                formatter: function() {
                    return this.isFirst ? 'Low' : 'High';
                },
            },
            title: {
                text: getAxisTitle(false, orientation),
                useHTML: true,
                offset: 5,
                align: 'middle',
                style: labelTitleStyle,
            },
        },
        {
            min: 0,
            max: 100,
            tickInterval: 50,
            tickLength: 0,
            lineWidth: 1,
            lineColor: '#808080',
            opposite: true,
            title: {
                enabled: false,
            },
            labels: {
                enabled: true,
                useHTML: true,
                x: 5,
                style: labelTickStyle,
                formatter: function() {
                    if (this.isFirst) {
                        return 'High';
                    } else if (!this.isFirst && !this.isLast) {
                        return '50<sup>th</sup>';
                    }
                },
            },
        },
    ];
}

function getAxisTitle(isXAxis, orientation) {
    const achievementAxisText = '&larr; <b>ACHIEVEMENT</b> &rarr;';
    const growthAxisText = '&larr; <b>GROWTH</b> &rarr;';

    return orientation === QUADRANT_CHART_DEFAULT_ORIENTATION
        ? isXAxis
            ? achievementAxisText
            : growthAxisText
        : isXAxis
        ? growthAxisText
        : achievementAxisText;
}

export default {
    getYAxis,
    getXAxis,
    getSeries,
    labelTitleStyle,
};
